import Api from '@/api'
import JobPosting from '@/models/JobPosting'
import {
  buildListQuery,
  rejectWithResponse,
  unwrap,
} from '@/utils/storeHelpers'

// Company job postings (business admin side). Named `jobPosting`, deliberately
// not `job` — the Schedule module's "Job" is a different domain (shifts).
//
// Actions reject with `err.response` (repo convention), so callers feed the
// rejection to `Form.$handleError({ response })` for 422 field errors.

// Run a request that returns one job, and make it the current job.
function setCurrentFrom(commit, request) {
  return rejectWithResponse(async () => {
    const job = unwrap(await request())
    commit('setCurrentJob', job)
    return job
  })
}

export default {
  namespaced: true,

  state: {
    jobs: [],
    jobsMeta: {},
    currentJob: null,
    applications: [],
    applicationsMeta: {},
    filters: {},
    // Page of the last list fetch, so coming back from a job reopens the same page.
    page: 1,
    insights: null,
    jobInsights: null,
  },

  getters: {
    page: (state) => state.page,
    insights: (state) => state.insights,
    jobInsights: (state) => state.jobInsights,
    jobs: (state) => state.jobs,
    jobsMeta: (state) => state.jobsMeta,
    currentJob: (state) => state.currentJob,
    applications: (state) => state.applications,
    applicationsMeta: (state) => state.applicationsMeta,
    filters: (state) => state.filters,
  },

  mutations: {
    setJobs(state, jobs) {
      state.jobs = jobs
    },
    setJobsMeta(state, meta) {
      state.jobsMeta = meta
    },
    setCurrentJob(state, job) {
      state.currentJob = job
    },
    clearCurrentJob(state) {
      state.currentJob = null
      state.applications = []
      state.applicationsMeta = {}
    },
    setApplications(state, applications) {
      state.applications = applications
    },
    setApplicationsMeta(state, meta) {
      state.applicationsMeta = meta
    },
    setFilters(state, filters) {
      state.filters = filters || {}
    },
    setPage(state, page) {
      state.page = page || 1
    },
    setInsights(state, insights) {
      state.insights = insights
    },
    setJobInsights(state, insights) {
      state.jobInsights = insights
    },
    // Swap the updated application in place so the list doesn't refetch.
    replaceApplication(state, application) {
      state.applications = state.applications.map((a) =>
        a.id === application.id ? { ...a, ...application } : a,
      )
    },
    removeJob(state, id) {
      state.jobs = state.jobs.filter((j) => String(j.id) !== String(id))
    },
  },

  actions: {
    fetchJobs({ commit }, payload = {}) {
      commit('setFilters', payload.filters || {})
      commit('setPage', payload.page)
      return rejectWithResponse(async () => {
        const res = await buildListQuery(
          JobPosting,
          ['status', 'q'],
          payload,
        ).get()
        commit('setJobs', res.data || [])
        commit('setJobsMeta', res.meta || {})
        return res
      })
    },

    fetchJob({ commit }, id) {
      commit('clearCurrentJob')
      return setCurrentFrom(commit, () => Api.get(`job-postings/${id}`))
    },

    createJob({ commit }, payload) {
      return setCurrentFrom(commit, () => Api.post('job-postings', payload))
    },

    updateJob({ commit }, { id, payload }) {
      return setCurrentFrom(commit, () =>
        Api.put(`job-postings/${id}`, payload),
      )
    },

    publishJob({ commit }, id) {
      return setCurrentFrom(commit, () =>
        Api.post(`job-postings/${id}/publish`),
      )
    },

    // notifyUnsuccessful: also push everyone who was not progressed.
    closeJob({ commit }, { id, notifyUnsuccessful = false }) {
      return setCurrentFrom(commit, () =>
        Api.post(`job-postings/${id}/close`, {
          notify_unsuccessful: !!notifyUnsuccessful,
        }),
      )
    },

    // Who did what on a job, newest first.
    fetchHistory(ctx, id) {
      return rejectWithResponse(async () => {
        const res = await Api.get(`job-postings/${id}/history`)
        return unwrap(res) || []
      })
    },

    // The company's projects, for linking a job to one. Fetched straight from
    // the API so the shared project list in the `project` store is untouched.
    fetchProjectOptions() {
      return rejectWithResponse(async () => {
        const res = await Api.get('projects', {
          params: { page: 1, limit: 100, is_archived: 0 },
        })
        const body = res.data || {}
        return Array.isArray(body.data) ? body.data : []
      })
    },

    // Jobs linked to one project in a given status group (published, expiring
    // or expired). Returned, not stored: the Jobs list page keeps its own state.
    fetchProjectJobs(ctx, { projectId, status }) {
      return rejectWithResponse(async () => {
        const res = await Api.get('job-postings', {
          params: {
            'filter[project_id]': projectId,
            'filter[status]': status,
            per_page: 50,
          },
        })
        const body = res.data || {}
        return Array.isArray(body.data) ? body.data : []
      })
    },

    // Tradies this company has marked not suited, one entry per job (they no
    // longer see that job).
    fetchNotSuited() {
      return rejectWithResponse(async () => {
        const res = await Api.get('job-postings/not-suited')
        return unwrap(res) || []
      })
    },

    markNotSuited({ commit }, { jobId, applicationId }) {
      return rejectWithResponse(async () => {
        await Api.post(
          `job-postings/${jobId}/applications/${applicationId}/not-suited`,
        )
        commit('replaceApplication', { id: applicationId, status: 'rejected' })
      })
    },

    // Private company notes on one applicant, newest first. Returned, not
    // stored: the notes panel keeps its own list.
    fetchApplicationNotes(ctx, { jobId, applicationId }) {
      return rejectWithResponse(async () => {
        const res = await Api.get(
          `job-postings/${jobId}/applications/${applicationId}/notes`,
        )
        return unwrap(res) || []
      })
    },

    addApplicationNote(ctx, { jobId, applicationId, body }) {
      return rejectWithResponse(async () => {
        const res = await Api.post(
          `job-postings/${jobId}/applications/${applicationId}/notes`,
          { body },
        )
        return unwrap(res)
      })
    },

    deleteApplicationNote(ctx, { jobId, applicationId, noteId }) {
      return rejectWithResponse(async () => {
        await Api.delete(
          `job-postings/${jobId}/applications/${applicationId}/notes/${noteId}`,
        )
      })
    },

    // Undo a rejection: back to "new", and any not-suited mark for this job is
    // lifted by the API.
    reinstateApplication({ commit }, { jobId, applicationId }) {
      return rejectWithResponse(async () => {
        const res = await Api.post(
          `job-postings/${jobId}/applications/${applicationId}/reinstate`,
        )
        const application = unwrap(res)
        if (application && application.id) {
          commit('replaceApplication', application)
        }
        return application
      })
    },

    removeNotSuited(ctx, entryId) {
      return rejectWithResponse(async () => {
        await Api.delete(`job-postings/not-suited/${entryId}`)
      })
    },

    deleteJob({ commit }, id) {
      return rejectWithResponse(async () => {
        await Api.delete(`job-postings/${id}`)
        commit('removeJob', id)
      })
    },

    // Company-wide numbers for the list page.
    fetchInsights({ commit }) {
      return rejectWithResponse(async () => {
        const res = await Api.get('job-postings/insights')
        commit('setInsights', unwrap(res))
      })
    },

    // One job's numbers; counted server-side over every application.
    fetchJobInsights({ commit }, id) {
      commit('setJobInsights', null)
      return rejectWithResponse(async () => {
        const res = await Api.get(`job-postings/${id}/insights`)
        commit('setJobInsights', unwrap(res))
      })
    },

    fetchApplications({ commit }, { id, status, q, page, perPage } = {}) {
      return rejectWithResponse(async () => {
        const params = {}
        if (status) params['filter[status]'] = status
        if (q) params['filter[q]'] = q
        if (page) params.page = page
        if (perPage) params.per_page = perPage
        const res = await Api.get(`job-postings/${id}/applications`, {
          params,
        })
        const body = res.data || {}
        const list = Array.isArray(body.data) ? body.data : []
        commit('setApplications', list)
        commit('setApplicationsMeta', body.meta || {})
        return list
      })
    },

    // A short-lived signed URL to the resume the applicant chose to share.
    fetchApplicantResume(ctx, { jobId, applicationId }) {
      return rejectWithResponse(async () => {
        const res = await Api.get(
          `job-postings/${jobId}/applications/${applicationId}/resume`,
        )
        const data = unwrap(res)
        return data && data.resume_url
      })
    },

    // requestContact ("Shortlist & Contact"): shortlist and ask the tradie to
    // share their phone and email for this application.
    updateApplicationStatus(
      { commit },
      { jobId, applicationId, status, requestContact = false },
    ) {
      return rejectWithResponse(async () => {
        const body = { status }
        if (requestContact) body.request_contact = true
        const res = await Api.patch(
          `job-postings/${jobId}/applications/${applicationId}`,
          body,
        )
        const application = unwrap(res)
        if (application && application.id) {
          commit('replaceApplication', application)
        }
        return application
      })
    },
  },
}
